import { HttpClient } from '@angular/common/http';
import { Injectable, EventEmitter } from '@angular/core';
import { makeStateKey, TransferState } from '@angular/core';
import { PlatformInformationService } from '../../../../projects/platform-manager/src/public_api';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import { Shop } from '../entities/shop';
import { TranslateService } from '@ngx-translate/core';
import { SurrogateKeyService } from '../../core/services/surrogate-key.service';

@Injectable({
  providedIn: 'root'
})
export class ShopDataService {
  readonly preLoadLocationBySlug = new EventEmitter<string>();
  readonly onLoadLocationBySlug = new EventEmitter<Shop>();
  readonly onLoadLocationBySlugError = new EventEmitter<string>();

  constructor(
    private platformService: PlatformInformationService,
    private http: HttpClient,
    private transferState: TransferState,
    private translateService: TranslateService,
    private surrogateKeyService: SurrogateKeyService
  ) {}

  getLocationBySlug(slug: string) {
    this.preLoadLocationBySlug.emit();
    this.getLocationBySlugData(slug).subscribe({
      next: (shop: Shop) => {
        this.onLoadLocationBySlug.emit(shop);
      },
      error: error => {
        console.error('could not load shop for slug ' + slug, error);
        this.onLoadLocationBySlugError.emit(slug);
      }
    });
  }

  private getLocationBySlugData(slug: string): Observable<Shop> {
    const locationBySlugKey = makeStateKey<Shop>('shop-' + slug);
    if (this.transferState.hasKey(locationBySlugKey)) {
      const shop = this.transferState.get<Shop>(locationBySlugKey, null);
      this.transferState.remove(locationBySlugKey);
      return of(shop);
    }

    const url = this.platformService
      .getApiEndpointFor('getLocationBySlug')
      .replace('%lang%', this.translateService.currentLang)
      .replace('%slug%', slug);

    return this.http
      .get<Shop>(url, {
        headers: { Accept: 'application/json' },
        observe: 'response'
      })
      .pipe(
        map(response => {
          if (!this.platformService.getIsBrowser() && response.status === 200) {
            const ids = [];
            if (response.body.shopId) {
              ids.push('shop-' + response.body.shopId);
            }
            if (response.body.companyId) {
              ids.push('company-' + response.body.companyId);
            }
            if (response.body.townId) {
              ids.push('town-' + response.body.townId);
            }
            this.surrogateKeyService.addSurrogateKeys(ids);
            // the server has loaded the shops. cache them in the transferstate for the client
            this.transferState.set(locationBySlugKey, response.body);
          }
          return response.body;
        }),
        catchError(() => {
          return throwError(() => {
            new Error('Could not load locations by slug: ' + url);
          });
        })
      );
  }
}
